import { decamelizeKeys } from 'humps'
import { map } from 'lodash'

import { currentOrderUuid } from '../utils/orderNumbers'

import BaseRemote from './baseRemote'
import { getDeviceId, getDeviceSerial } from '../utils/deviceData'

// limit request time for scenarios where the interenet conneciton is unreliable beying usability
const TIMEOUT_REQUEST_INTERVAL = 36_000
const TIMEOUT_FETCH_INTERVAL = 20_000

export const instance = new BaseRemote(process.env.REACT_APP_BASE_URL)

class StadiumRemote {
  static deviceRoute = (isKiosk) => (isKiosk ? '/kiosk/v3/orders' : '/pos/orders')

  static createPosOrder(params = {}, isKiosk, callback = (response) => {}) {
    const response = instance.post(`${StadiumRemote.deviceRoute(isKiosk)}`, params, { timeout: TIMEOUT_REQUEST_INTERVAL })
    callback(response)

    return response
  }

  static getOrderDeltas = (venueUuid, sinceTimestamp) => {
    let payload = decamelizeKeys({
      params: { venueUuid },
    })

    if (sinceTimestamp) {
      payload.params.since_timestamp = sinceTimestamp
    }

    return instance.get('/pos/data_deltas/order', payload)
  }

  static getOrders = ({ venueUuid, standMenuUuids, updatedSince }) => {
    // These have to be sent in params. The backing library to Axios will not allow a body.
    const payload = decamelizeKeys({
      params: { venueUuid, standMenuUuids, updatedSince },
    })
    return instance.get('/pos/orders', payload)
  }

  static getOrderDetails = (orderUuid, isRefundReceiptsEnabled) => {
    /**
     * isRefundReceiptsEnabled is as a feature flag for refund receipts
     * keep in sync with useGetEnvVars hook
     */
    if (isRefundReceiptsEnabled) {
      const payload = decamelizeKeys({
        params: { includeRefundedLineItems: true },
      })
      return instance.get(`/pos/orders/${orderUuid}`, payload)
    }

    return instance.get(`/pos/orders/${orderUuid}`)
  }

  static updatePosOrder(uuid, params = {}, isKiosk) {
    return instance.put(`${StadiumRemote.deviceRoute(isKiosk)}/${uuid}`, params, { timeout: TIMEOUT_REQUEST_INTERVAL })
  }

  static fetchMenus = ({ organizationName, standUuids, paymentType }) => {
    let translatedPaymentType = paymentType ? (paymentType === 'fcc' ? 'freedompay' : 'shift4') : null
    var payload = {
      params: { organizationName, venueUuid: process.env.REACT_APP_VENUE_UUID, paymentType: translatedPaymentType },
    }

    if (standUuids !== undefined && standUuids !== null) {
      payload = {
        params: { organizationName, uuids: standUuids, paymentType: translatedPaymentType },
      }
    }

    return instance.get('/pos/stands', payload)
  }

  static fetchPrinters = () => {
    var payload = {
      params: { organizationName: process.env.REACT_APP_ORG, venueUuid: process.env.REACT_APP_VENUE_UUID },
    }

    return instance.get('/pos/printers', payload)
  }

  static addLineItems = (orderUuid, orderMenuItems, isKiosk) => {
    const payload = { orderUuid, orderMenuItems }
    return instance.post(`${StadiumRemote.deviceRoute(isKiosk)}/${orderUuid}/line_items`, payload)
  }

  static closeTabOrder = (orderId, receiptSignature, tipAmountInCents, payments, isKiosk) => {
    const payload = {
      uuid: orderId,
      receiptSignature,
      tipAmountInCents,
      payments,
    }

    return instance.post(`${StadiumRemote.deviceRoute(isKiosk)}/${orderId}/close`, payload)
  }

  static refundOrderItems = (orderUuid, reason, lineItemUuids) => {
    const payload = {
      orderUuid,
      reason,
      userDetails: {},
      lineItemUuids,
    }

    return instance.post('/pos/refund', payload)
  }

  static voidItems = (orderUuid, reason, lineItemUuids) => {
    const payload = {
      data: {
        orderUuid,
        reason,
        userDetails: {},
        lineItemUuids,
      },
    }

    return instance.delete(`/pos/orders/${orderUuid}/line_items`, payload)
  }

  static getAttendants = (organizationName) => {
    // Ensure there is an access token when requesting attendants
    if (!localStorage.getItem('accessToken')) {
      return Promise.reject(new Error('Access token not found'))
    }

    const payload = {
      params: {
        organizationName: process.env.REACT_APP_ORG,
        venueUuid: process.env.REACT_APP_VENUE_UUID,
      },
    }

    return instance.get('/operator/attendants', payload)
  }

  static getAttendantSettings = (pinNumber) => {
    const payload = {
      params: { pinNumber, venueUuid: process.env.REACT_APP_VENUE_UUID },
    }

    return instance.get('/api/v1/attendant_settings', payload)
  }

  static getPromotions = (venueUuid, standMenuUuids) => {
    const payload = {
      params: { venueUuid, standMenuUuids },
    }

    return instance.get('/pos/promotions', payload)
  }

  static getRemoteOrderTotal = (
    orderMenuItems,
    standMenuUuid,
    promotions,
    configOverride = {},
    isKiosk,
    affiliations = [],
    userUuid = undefined,
    taxExempt = false,
  ) => {
    let payload = {
      affiliations,
      userUuid,
      promotions: !promotions
        ? undefined
        : map(promotions, (promotion) => ({ uuid: promotion.uuid, approverUuid: promotion?.approverUuid })),
      orderMenuItems,
      standMenuUuid,
      taxExempt, // TODO Juan T44632: Retest because of previous comment: "This may need to be removed since changing this flag resets any promotions in Moneyball Validate when plat is working"
    }

    payload.uuid = currentOrderUuid()

    return instance.post(`${StadiumRemote.deviceRoute(isKiosk)}/total`, payload, configOverride)
  }

  static getSummaryReport = (employeeUuid) => {
    const serialNumber = getDeviceSerial(false) || undefined
    const deviceId = getDeviceId()
    const payload = {
      params: {
        employeeUuid,
      },
    }

    if (serialNumber) {
      payload.params.serialNumber = serialNumber
    } else {
      payload.params.deviceId = deviceId
    }

    return instance.get('/pos/order_reports', payload)
  }

  static sendEmailJobToStadium = ({ orderUuid, emails, paymentUuid }) => {
    const payload = decamelizeKeys({
      emails,
      paymentUuid,
    })
    return instance.post(`/pos/orders/${orderUuid}/emails/PosOrderReceiptEmailJob`, payload)
  }

  static getTicketBalance = (ticketId) => {
    return instance.get(`/pos/tickets/${ticketId}`)
  }

  static getQRBalance = (qrCode) => {
    return instance.get(`/pos/nonces/${qrCode}`)
  }

  static getGiftCardBalance = (giftCardId) => {
    return instance.get(`/pos/gift_cards/${giftCardId}`)
  }

  // config requests
  static getConfig = () => instance.get(`/api/v1/app_device_configurations`)

  static createConfig = (params = {}) => {
    const payload = decamelizeKeys({
      ...params,
    })

    return instance.post('/api/v1/app_device_configurations', payload)
  }

  static updateConfig = (params = {}) => {
    const payload = decamelizeKeys({
      ...params,
    })

    return instance.put(`/api/v1/app_device_configurations?uuid=${params.uuid}`, payload)
  }

  static getSections = (standUuid) => {
    // If the stand UUID is undefined, don't attempt to make an API call, which would result in an
    // error due to a string being passed as a parameter.
    if (!standUuid) {
      return new Promise((resolve, reject) => {
        resolve({ data: [] })
      })
    }

    return instance.get(`/pos/stands/${standUuid}/sections`)
  }

  static getRows = (sectionName) => {
    return instance.get(`/pos/sections/${sectionName}/rows?venue_uuid=${process.env.REACT_APP_VENUE_UUID}`)
  }

  static getSeats = (sectionName, row) => {
    return instance.get(`/pos/sections/${sectionName}/rows/${row}/seats?venue_uuid=${process.env.REACT_APP_VENUE_UUID}`)
  }

  static getPosMenu(menuUuid, organizationName, isKiosk) {
    if (!isKiosk) {
      return instance.get(`pos/menus/${menuUuid}?organization_name=${organizationName}&include_open_price_items=true`, {
        timeout: TIMEOUT_FETCH_INTERVAL,
      })
    }
    return instance.get(`pos/menus/${menuUuid}?organization_name=${organizationName}`)
  }

  static getTaxes() {
    return instance.get('/pos/taxes', {
      params: { venueUuid: process.env.REACT_APP_VENUE_UUID },
    })
  }
}

export default StadiumRemote
